/**
 * Shared settings dialog for exposed value modules.
 * Pass `translationPrefix` (e.g. "mod_energy.energy_management") and fallback strings.
 */

import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/ui/accordion";
import { Separator } from "@/shared/ui/separator";
import { Cpu, GitBranch, Info, Layers, ListTree } from "lucide-react";
import { useLanguage } from "@/core/LanguageContext";
import { ExposedValueGrid } from "@/core/components/exposed-values/ExposedValueGrid";
import type {
  Area,
  ExposedValueDefinition,
  ExposedValueMapping,
  Line,
  LocationScope,
  RealTimeValue,
  Station,
} from "@/core/types/exposed-values";

interface ExposedValueSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  translationPrefix: string;
  titleFallback: string;
  descriptionFallback: string;
  exposedValues: ExposedValueDefinition[];
  factoryScope: LocationScope;
  areas: Area[];
  lines: Line[];
  stations: Station[];
  hasDatapoints: boolean;
  realTimeValues: Record<string, RealTimeValue>;
  getMappingForScope: (scope: LocationScope, exposedValueId: string) => ExposedValueMapping | null;
  onSelectMapping: (scope: LocationScope, definition: ExposedValueDefinition) => void;
  onOpenChart: (mapping: ExposedValueMapping) => void;
}

export const ExposedValueSettingsDialog = ({
  open,
  onOpenChange,
  translationPrefix,
  titleFallback,
  descriptionFallback,
  exposedValues,
  factoryScope,
  areas,
  lines,
  stations,
  hasDatapoints,
  realTimeValues,
  getMappingForScope,
  onSelectMapping,
  onOpenChart,
}: ExposedValueSettingsDialogProps) => {
  const { t } = useLanguage();
  const [settingsTab, setSettingsTab] = useState("overview");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t(`${translationPrefix}.settings.title`, titleFallback)}</DialogTitle>
          <DialogDescription>
            {t(`${translationPrefix}.settings.description`, descriptionFallback)}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 h-full">
          <div className="space-y-2">
            <Button
              variant={settingsTab === "overview" ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => setSettingsTab("overview")}
            >
              <Info className="h-4 w-4 mr-2" />
              {t(`${translationPrefix}.settings.nav.overview`, "Overview")}
            </Button>
            <Button
              variant={settingsTab === "exposed" ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => setSettingsTab("exposed")}
            >
              <Layers className="h-4 w-4 mr-2" />
              {t(`${translationPrefix}.settings.nav.exposed`, "Exposed Values")}
            </Button>
            <Button
              variant={settingsTab === "hierarchy" ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => setSettingsTab("hierarchy")}
            >
              <ListTree className="h-4 w-4 mr-2" />
              {t(`${translationPrefix}.settings.nav.hierarchy`, "Factory Hierarchy")}
            </Button>
          </div>
          <ScrollArea className="h-[60vh] pr-4">
            {settingsTab === "overview" && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {t(`${translationPrefix}.settings.overview.title`, "Welcome to module settings")}
                    </CardTitle>
                    <CardDescription>
                      {t(
                        `${translationPrefix}.settings.overview.subtitle`,
                        "Exposed values define the contract between signals and the dashboard."
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm text-muted-foreground">
                    <p>
                      {t(
                        `${translationPrefix}.settings.overview.body`,
                        "Map datapoints to unlock KPIs, trends, and comparisons across the factory hierarchy."
                      )}
                    </p>
                    <Separator />
                    <div className="grid gap-3 md:grid-cols-3">
                      <Card className="border-dashed">
                        <CardContent className="pt-4 space-y-2">
                          <p className="text-xs uppercase text-muted-foreground">
                            {t(`${translationPrefix}.settings.overview.step1.label`, "Step 1")}
                          </p>
                          <p className="font-medium text-foreground">
                            {t(
                              `${translationPrefix}.settings.overview.step1.title`,
                              "Review exposed values"
                            )}
                          </p>
                          <p>
                            {t(
                              `${translationPrefix}.settings.overview.step1.description`,
                              "Verify the values required for this module."
                            )}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="border-dashed">
                        <CardContent className="pt-4 space-y-2">
                          <p className="text-xs uppercase text-muted-foreground">
                            {t(`${translationPrefix}.settings.overview.step2.label`, "Step 2")}
                          </p>
                          <p className="font-medium text-foreground">
                            {t(
                              `${translationPrefix}.settings.overview.step2.title`,
                              "Map datapoints"
                            )}
                          </p>
                          <p>
                            {t(
                              `${translationPrefix}.settings.overview.step2.description`,
                              "Assign live datapoints at any hierarchy level."
                            )}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="border-dashed">
                        <CardContent className="pt-4 space-y-2">
                          <p className="text-xs uppercase text-muted-foreground">
                            {t(`${translationPrefix}.settings.overview.step3.label`, "Step 3")}
                          </p>
                          <p className="font-medium text-foreground">
                            {t(
                              `${translationPrefix}.settings.overview.step3.title`,
                              "Watch KPIs update"
                            )}
                          </p>
                          <p>
                            {t(
                              `${translationPrefix}.settings.overview.step3.description`,
                              "The dashboard updates instantly as mappings are added."
                            )}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {settingsTab === "exposed" && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {t(`${translationPrefix}.settings.exposed.title`, "Exposed Values")}
                    </CardTitle>
                    <CardDescription>
                      {t(
                        `${translationPrefix}.settings.exposed.subtitle`,
                        "These values define the signals required by the module."
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {exposedValues.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t(
                          `${translationPrefix}.settings.exposed.empty`,
                          "No exposed values registered for this module."
                        )}
                      </p>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2">
                        {exposedValues.map((definition) => (
                          <Card key={definition.id} className="border-border/60">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base">{definition.display_name}</CardTitle>
                              <CardDescription>
                                {definition.description ||
                                  t(`${translationPrefix}.metric.noDescription`, "No description")}
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="text-xs text-muted-foreground">
                              {definition.name}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {settingsTab === "hierarchy" && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {t(`${translationPrefix}.settings.hierarchy.title`, "Hierarchy Mapping")}
                    </CardTitle>
                    <CardDescription>
                      {t(
                        `${translationPrefix}.settings.hierarchy.subtitle`,
                        "Assign exposed values at each factory level."
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary">
                          {t(`${translationPrefix}.settings.hierarchy.factory`, "Factory")}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{factoryScope.label}</span>
                      </div>
                      <ExposedValueGrid
                        translationPrefix={translationPrefix}
                        definitions={exposedValues}
                        scope={factoryScope}
                        getMapping={getMappingForScope}
                        realTimeValues={realTimeValues}
                        onSelect={(definition) => onSelectMapping(factoryScope, definition)}
                        onOpenChart={onOpenChart}
                        isSelectionDisabled={!hasDatapoints}
                      />
                    </div>
                    <Separator />
                    {areas.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t(`${translationPrefix}.settings.hierarchy.empty`, "No areas configured yet.")}
                      </p>
                    ) : (
                      <Accordion type="single" collapsible className="w-full">
                        {areas.map((area) => {
                          const areaScope: LocationScope = {
                            type: "area",
                            id: area.id,
                            label: area.name,
                          };
                          const areaLines = lines.filter((line) => line.area_id === area.id);
                          return (
                            <AccordionItem key={area.id} value={area.id}>
                              <AccordionTrigger className="text-sm">
                                <div className="flex items-center gap-2">
                                  <Layers className="h-4 w-4 text-muted-foreground" />
                                  <span>{area.name}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {areaLines.length}
                                  </Badge>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="space-y-4">
                                <ExposedValueGrid
                                  translationPrefix={translationPrefix}
                                  definitions={exposedValues}
                                  scope={areaScope}
                                  getMapping={getMappingForScope}
                                  realTimeValues={realTimeValues}
                                  onSelect={(definition) => onSelectMapping(areaScope, definition)}
                                  onOpenChart={onOpenChart}
                                  isSelectionDisabled={!hasDatapoints}
                                />
                                {areaLines.map((line) => {
                                  const lineScope: LocationScope = {
                                    type: "line",
                                    id: line.id,
                                    label: line.name,
                                  };
                                  const lineStations = stations.filter(
                                    (station) => station.line_id === line.id
                                  );
                                  return (
                                    <div key={line.id} className="space-y-3">
                                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <GitBranch className="h-3 w-3" />
                                        <span>{line.name}</span>
                                        <Badge variant="outline" className="text-[10px]">
                                          {lineStations.length}
                                        </Badge>
                                      </div>
                                      <ExposedValueGrid
                                        translationPrefix={translationPrefix}
                                        definitions={exposedValues}
                                        scope={lineScope}
                                        getMapping={getMappingForScope}
                                        realTimeValues={realTimeValues}
                                        onSelect={(definition) => onSelectMapping(lineScope, definition)}
                                        onOpenChart={onOpenChart}
                                        isSelectionDisabled={!hasDatapoints}
                                      />
                                      {lineStations.map((station) => {
                                        const stationScope: LocationScope = {
                                          type: "station",
                                          id: station.id,
                                          label: station.name,
                                        };
                                        return (
                                          <div key={station.id} className="space-y-2">
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                              <Cpu className="h-3 w-3" />
                                              <span>{station.name}</span>
                                            </div>
                                            <ExposedValueGrid
                                              translationPrefix={translationPrefix}
                                              definitions={exposedValues}
                                              scope={stationScope}
                                              getMapping={getMappingForScope}
                                              realTimeValues={realTimeValues}
                                              onSelect={(definition) =>
                                                onSelectMapping(stationScope, definition)
                                              }
                                              onOpenChart={onOpenChart}
                                              isSelectionDisabled={!hasDatapoints}
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};
